import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { getAccessToken } from "../../awsAuth";
import "./ShareModal.scss";

const DEFAULT_AVATAR = "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";
const API_BASE = "/api/messages";

function useBodyScrollLock(lock) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (lock) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [lock]);
}

const SHARE_FRIENDS_CACHE_TTL = 5 * 60 * 1000;

function safeLoadShareCache(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.expires && parsed.expires < Date.now()) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed.data || null;
  } catch {
    return null;
  }
}

function safeSaveShareCache(key, data) {
  if (typeof window === "undefined") return;
  try {
    const payload = {
      data,
      expires: Date.now() + SHARE_FRIENDS_CACHE_TTL,
    };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
  }
}

export default function ShareModal({ open, onClose, shareUrl, shareText, onShared }) {
  const { userId } = useAuth();
  useBodyScrollLock(open);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 640px)").matches
      : false
  );
  const [sheetState, setSheetState] = useState("half");
  const sheetRef = useRef(null);
  const headerRef = useRef(null);
  const dragRef = useRef({
    startY: 0,
    startH: 0,
    dragging: false,
    lastDelta: 0,
    startTime: 0,
    endTime: 0,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mm = window.matchMedia("(max-width: 640px)");
    const handler = () => setIsMobile(mm.matches);
    if (mm.addEventListener) mm.addEventListener("change", handler);
    else mm.addListener(handler);
    return () => {
      if (mm.removeEventListener) mm.removeEventListener("change", handler);
      else mm.removeListener(handler);
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const [friends, setFriends] = useState([]);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadFollowing = useCallback(async () => {
    if (!userId) return;
    setError(null);

    const cacheKey = `share:friends:${userId}`;
    const cached = safeLoadShareCache(cacheKey);

    if (cached && Array.isArray(cached)) {
      setFriends(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get(
        `/api/follow/following/${encodeURIComponent(userId)}`,
        { validateStatus: () => true }
      );
      const ids = Array.isArray(data?.followingIds) ? data.followingIds : [];

      const poolSize = 6;
      const chunks = [];
      for (let i = 0; i < ids.length; i += poolSize) {
        chunks.push(ids.slice(i, i + poolSize));
      }

      const results = [];
      for (const chunk of chunks) {
        const arr = await Promise.all(
          chunk.map(async (uid) => {
            try {
              const r = await axios.get(
                `/api/account/${encodeURIComponent(uid)}`,
                { validateStatus: () => true }
              );
              const u = r?.data || {};
              return {
                id: uid,
                username:
                  u.username ||
                  u.userName ||
                  u.handle ||
                  u.login ||
                  u.nick ||
                  null,
                name:
                  u.name ||
                  u.displayName ||
                  u.fullName ||
                  u.firstName ||
                  null,
                avatar:
                  u.avatar ||
                  u.avatarUrl ||
                  u.photo ||
                  u.picture ||
                  DEFAULT_AVATAR,
              };
            } catch {
              return {
                id: uid,
                username: null,
                name: null,
                avatar: DEFAULT_AVATAR,
              };
            }
          })
        );
        results.push(...arr);
      }

      const map = new Map();
      for (const it of results) if (it?.id) map.set(it.id, it);
      const finalFriends = Array.from(map.values());

      setFriends(finalFriends);
      safeSaveShareCache(cacheKey, finalFriends);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e.message ||
          "Не вдалося завантажити список"
      );
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open) {
      setSelected([]);
      setQuery("");
      setCopied(false);
      setSheetState("half");
      loadFollowing();
    }
  }, [open, loadFollowing]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return friends;
    return friends.filter((f) => {
      const u = (f.username || "").toLowerCase();
      const n = (f.name || "").toLowerCase();
      return u.includes(q) || n.includes(q);
    });
  }, [friends, query]);

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
      alert("Не вдалося скопіювати посилання");
    }
  };

  const handleSend = async () => {
    if (!selected.length || !userId) return;
    setSending(true);
    try {
      const rawText = (shareText || "").trim();
      const payloadText = rawText
        ? `${rawText}\n${shareUrl}`
        : shareUrl || "";

      const token = await getAccessToken();
      for (const toUserId of selected) {
        await axios.post(
          `${API_BASE}/chat/send`,
          { to: toUserId, text: payloadText },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      try {
        onShared?.(selected);
      } catch {}

      setSelected([]);
      onClose?.();
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e.message ||
        "Не вдалося надіслати повідомлення";
      alert(msg);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!isMobile || !open) return;
    const sheet = sheetRef.current;
    if (sheet)
      sheet.style.setProperty(
        "--sm-sheet-height",
        sheetState === "full" ? "100vh" : "62vh"
      );
  }, [isMobile, open, sheetState]);

  const onGrabStart = (clientY) => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    dragRef.current = {
      startY: clientY,
      startH: sheetState === "full" ? 100 : 62,
      dragging: true,
      lastDelta: 0,
      startTime: performance.now(),
      endTime: 0,
    };
    sheet.classList.add("sm-modal--dragging");
  };

  const onGrabMove = (clientY) => {
    if (!dragRef.current.dragging) return;
    const deltaY = clientY - dragRef.current.startY;
    dragRef.current.lastDelta = deltaY;
    const screenH = Math.max(window.innerHeight, 1);
    const deltaVH = (deltaY / screenH) * 100;
    let nextVH = dragRef.current.startH - deltaVH;
    nextVH = Math.max(46, Math.min(100, nextVH));
    const sheet = sheetRef.current;
    if (sheet)
      sheet.style.setProperty("--sm-sheet-height", `${nextVH}vh`);
  };

  const onGrabEnd = () => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    const delta = dragRef.current.lastDelta;
    const sheet = sheetRef.current;
    if (sheet) sheet.classList.remove("sm-modal--dragging");

    const CLOSE_DRAG_PX = 90;
    const SNAP_DRAG_PX = 70;

    if (sheetState === "half") {
      if (delta > CLOSE_DRAG_PX) {
        onClose?.();
        return;
      }
      if (-delta > SNAP_DRAG_PX) {
        setSheetState("full");
        requestAnimationFrame(() =>
          sheet?.style.setProperty("--sm-sheet-height", "100vh")
        );
        return;
      }
      requestAnimationFrame(() =>
        sheet?.style.setProperty("--sm-sheet-height", "62vh")
      );
    } else {
      if (delta > SNAP_DRAG_PX) {
        setSheetState("half");
        requestAnimationFrame(() =>
          sheet?.style.setProperty("--sm-sheet-height", "62vh")
        );
        return;
      }
      requestAnimationFrame(() =>
        sheet?.style.setProperty("--sm-sheet-height", "100vh")
      );
    }
  };

  useEffect(() => {
    if (!isMobile || !open) return;
    const el = headerRef.current;
    if (!el) return;

    const onPointerDown = (e) => {
      e.preventDefault();
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      onGrabStart(y);
      window.addEventListener("pointermove", onPointerMove, {
        passive: false,
      });
      window.addEventListener("pointerup", onPointerUp, { passive: false });
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("touchend", onTouchEnd, { passive: false });
    };
    const onPointerMove = (e) => onGrabMove(e.clientY);
    const onPointerUp = () => {
      onGrabEnd();
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
    const onTouchMove = (e) => onGrabMove(e.touches[0].clientY);
    const onTouchEnd = () => {
      onGrabEnd();
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };

    el.addEventListener("pointerdown", onPointerDown, { passive: false });
    el.addEventListener("touchstart", onPointerDown, { passive: false });

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("touchstart", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [isMobile, open, sheetState]);

  if (!open) return null;

  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className={`sm-backdrop ${isMobile ? "is-mobile" : ""}`}
      onClick={onBackdropClick}
    >
      <div
        ref={sheetRef}
        className={`sm-modal ${isMobile ? `is-${sheetState}` : ""}`}
        role="dialog"
        aria-modal="true"
      >
        <div ref={headerRef} className="sm-header">
          <div className="sm-grabber" aria-hidden />
          <div className="sm-title">Поділитись</div>
          {!isMobile && (
            <button
              className="sm-close"
              onClick={onClose}
              aria-label="Закрити"
            >
              ✕
            </button>
          )}
        </div>

        <div className="sm-search">
          <input
            type="text"
            placeholder="Пошук серед ваших підписок…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="sm-content">
          {error && <div className="sm-error">⚠️ {error}</div>}

          {loading && (
            <div className="sm-grid sm-skeletonGrid">
              {Array.from({ length: 8 }).map((_, i) => (
                <div className="sm-skelCard" key={i}>
                  <div className="sm-skelAvatar" />
                  <div className="sm-skelName" />
                </div>
              ))}
            </div>
          )}

          {!loading && !error && friends.length === 0 && (
            <div className="sm-empty">У вас поки що немає підписок.</div>
          )}

          {!loading && !error && friends.length > 0 && (
            <div className="sm-grid">
              {filtered.map((u) => {
                const checked = selected.includes(u.id);
                return (
                  <button
                    key={u.id}
                    className={`sm-user ${checked ? "is-selected" : ""}`}
                    onClick={() => toggleSelect(u.id)}
                    title={u.username || u.name || "Користувач"}
                  >
                    <span className="sm-avatarWrap">
                      <img
                        className="sm-avatar"
                        src={u.avatar || DEFAULT_AVATAR}
                        alt=""
                      />
                      {checked && (
                        <span className="sm-check" aria-hidden>
                          ✓
                        </span>
                      )}
                    </span>
                    <span className="sm-username">
                      {u.username || u.name || "Користувач"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="sm-footer">
          <button className="sm-copy" onClick={handleCopy}>
            {copied ? "Скопійовано" : "Копіювати посилання"}
          </button>
          <button
            className="sm-send"
            disabled={!selected.length || sending}
            onClick={handleSend}
            title={
              selected.length
                ? `Надіслати (${selected.length})`
                : "Оберіть користувачів"
            }
          >
            {sending
              ? "Надсилання…"
              : `Надіслати ${selected.length ? `(${selected.length})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
